import { useState } from "react";
import { useAuth } from "../../../context";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../api/useFetchObjects";
import useAdd from "../../../api/useAdd";
import useUpdate from "../../../api/useUpdate";
import useDelete from "../../../api/useDelete";
import AcademicFormDialog from "./AcademicFormDialog";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TablePagination,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  MenuItem,
  Button,
  IconButton,
} from "@mui/material";
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Visibility as ViewIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

const academicTypeLabels = { 1: "Student", 2: "Intern", 3: "Resident" };
const statusLabels = { 1: "Active", 2: "On leave", 3: "Graduated", 4: "Withdrawn" };

export default function AcademicProfileList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const auth = useAuth();
  const token = auth?.user?.token || "";
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [typeFilter, setTypeFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAcademic, setEditingAcademic] = useState(null);

  const params = new URLSearchParams();
  params.set("page", page);
  params.set("page_size", rowsPerPage);
  if (typeFilter) params.set("academic_type", typeFilter);
  if (statusFilter) params.set("status", statusFilter);
  if (search && search.trim()) params.set("search", search.trim());
  const url = `academics/?${params.toString()}`;
  const { data, isLoading, isError, error } = useFetchObjects(
    ["academics", page, rowsPerPage, typeFilter, statusFilter, search],
    url,
    token
  );

  const { handleAdd } = useAdd("academics", token, t("academic.profileAdded", "Academic added."));
  const { handleUpdate } = useUpdate("academics", token);
  const { handleDelete, ConfirmDialog } = useDelete("academics", token);

  const rows = Array.isArray(data) ? data : (data?.results || []);
  const totalCount = Array.isArray(data) ? data.length : (data?.count || 0);

  const handleOpenAdd = () => { setEditingAcademic(null); setDialogOpen(true); };
  const handleOpenEdit = (row) => { setEditingAcademic(row); setDialogOpen(true); };
  const handleCloseDialog = () => { setDialogOpen(false); setEditingAcademic(null); };
  const handleSave = (payload) => {
    if (editingAcademic) handleUpdate(editingAcademic.id, payload);
    else handleAdd(payload);
    handleCloseDialog();
  };

  if (isError) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error?.message || "Failed to load academic profiles"}
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3, width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      <ConfirmDialog />
      <Paper sx={{ p: 3, width: "100%", maxWidth: "100%", overflow: "hidden" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", mb: 2, gap: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            {t("academic.profiles", "Academic Profiles")}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
            <TextField size="small" label={t("common.search", "Search")} placeholder={t("academic.searchPlaceholder", "Name, email, phone, ID…")} value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} sx={{ minWidth: 200 }} />
            <TextField select size="small" label={t("academic.type", "Type")} value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }} sx={{ minWidth: 120 }}>
              <MenuItem value="">{t("common.all", "All")}</MenuItem>
              <MenuItem value="1">{t("academic.student", "Student")}</MenuItem>
              <MenuItem value="2">{t("academic.intern", "Intern")}</MenuItem>
              <MenuItem value="3">{t("academic.resident", "Resident")}</MenuItem>
            </TextField>
            <TextField select size="small" label={t("academic.status", "Status")} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} sx={{ minWidth: 120 }}>
              <MenuItem value="">{t("common.all", "All")}</MenuItem>
              <MenuItem value="1">{t("academic.active", "Active")}</MenuItem>
              <MenuItem value="2">{t("academic.onLeave", "On leave")}</MenuItem>
              <MenuItem value="3">{t("academic.graduated", "Graduated")}</MenuItem>
              <MenuItem value="4">{t("academic.withdrawn", "Withdrawn")}</MenuItem>
            </TextField>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd}>
              {t("academic.addProfile", "Add Academic")}
            </Button>
          </Box>
        </Box>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}><CircularProgress /></Box>
        ) : (
          <>
            <TableContainer sx={{ width: "100%", overflow: "auto" }}>
              <Table size="small" sx={{ minWidth: 400 }} stickyHeader>
                <TableHead>
                  <TableRow sx={{ bgcolor: "grey.200" }}>
                    <TableCell><strong>#</strong></TableCell>
                    <TableCell><strong>{t("academic.name", "Name")}</strong></TableCell>
                    <TableCell><strong>{t("academic.traineeId", "Trainee ID")}</strong></TableCell>
                    <TableCell><strong>{t("academic.type", "Type")}</strong></TableCell>
                    <TableCell><strong>{t("academic.status", "Status")}</strong></TableCell>
                    <TableCell><strong>{t("academic.program", "Program")}</strong></TableCell>
                    <TableCell><strong>{t("academic.enrollmentDate", "Enrollment")}</strong></TableCell>
                    <TableCell align="right"><strong>{t("common.actions", "Actions")}</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}>{t("common.noRecords", "No records found.")}</TableCell></TableRow>
                  ) : (
                    rows.map((row, idx) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{(page - 1) * rowsPerPage + idx + 1}</TableCell>
                        <TableCell>
                          <Typography component="span" sx={{ cursor: "pointer", color: "primary.main", "&:hover": { textDecoration: "underline" } }} onClick={() => navigate(`/academic/profiles/view/${row.id}`)}>
                            {row.name_display || "—"}
                          </Typography>
                        </TableCell>
                        <TableCell>{row.trainee_id || "—"}</TableCell>
                        <TableCell><Chip size="small" label={row.academic_type_display || academicTypeLabels[row.academic_type]} /></TableCell>
                        <TableCell><Chip size="small" color={row.status === 1 ? "success" : row.status === 2 ? "warning" : "default"} label={row.status_display || statusLabels[row.status] || "—"} /></TableCell>
                        <TableCell>{row.program || "—"}</TableCell>
                        <TableCell>{row.enrollment_date || "—"}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => navigate(`/academic/profiles/view/${row.id}`)} title={t("common.view", "View")}><ViewIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleOpenEdit(row)} title={t("common.edit", "Edit")}><EditIcon fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => handleDelete(row.id)} color="error" title={t("common.delete", "Delete")}><DeleteIcon fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination component="div" count={totalCount} page={page - 1} onPageChange={(e, p) => setPage(p + 1)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(1); }} rowsPerPageOptions={[10, 25, 50]} />
          </>
        )}
      </Paper>
      <AcademicFormDialog open={dialogOpen} onClose={handleCloseDialog} academic={editingAcademic} onSave={handleSave} saving={false} />
    </Box>
  );
}
